import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import Tooltip from "aldoo-ra/Tooltip"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { AdjustmentsHorizontalIcon, PlusIcon } from "@heroicons/react/24/outline"
import { DashboardCatalog, ListDashboards, SaveDashboard, DeleteDashboard, QueryDashboard } from "./api"
import PeriodPicker from "./PeriodPicker"
import Select from "./Select"
import useLive from "./useLive"
import Filters, { ActiveFilters } from "./Filters"
import Advisor from "./Advisor"
import CardEditor from "./CardEditor"
import CustomizePanel from "./CustomizePanel"
import NameDialog from "aldoo-ra/Admin/AldooAds/NameDialog"
import MetricCard from "./cards/MetricCard"
import ChartCard from "./cards/ChartCard"
import TableCard from "./cards/TableCard"
import SegmentsCard from "./cards/SegmentsCard"
import useDashboardTexts from "./useDashboardTexts"
import { setMoneyCurrency } from "./format"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// how wide a card is on the dashboard's grid (12 columns from lg, 2 below)
const SPAN = { s: "col-span-12 sm:col-span-6 lg:col-span-3", m: "col-span-12 lg:col-span-6", l: "col-span-12" }
const ACTIVE_KEY = (scope) => `store-dashboard-active:${scope}`

/**
 * A user's store dashboards for a scope (a company's id, or "store"): cards
 * they compose — numbers compared with an earlier period, charts, tables —
 * over a period they pick, the way Google Analytics' reports work. Several
 * dashboards per user; every change is kept as it is made.
 */
/**
 * `headerClassName` is the sticky header's background: the page's own, so the
 * cards pass under it unseen (the admin's canvas by default). `stickyTop` is
 * where it stops: under a page's own fixed bar, when it has one.
 * `helpKeys`: the help center's articles behind the "?" hints (aldoo-ra
 * HelpCenter/InPlaceHelp), named by the project: { header, cards }.
 */
export default function StoreDashboards({ scope, className = "", headerClassName = "bg-admin_canvas", stickyTop = "top-0", advisor = {}, helpKeys = {} }) {
  const t = useDashboardTexts()
  const [catalog, setCatalog] = useState(null)
  const [dashboards, setDashboards] = useState(null)
  const [activeId, setActiveId] = useState(null)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [editing, setEditing] = useState(null) // a card, or "new"
  const [customizing, setCustomizing] = useState(false)
  const saveTimer = useRef(null)
  const queryRun = useRef(0)
  // a live refresh: the figures moved on the server, the cards follow quietly
  const [moved, setMoved] = useState(0)
  const quiet = useRef(false)
  useLive(scope, () => {
    quiet.current = true
    setMoved((n) => n + 1)
  })

  useEffect(() => {
    if (!scope) return
    setDashboards(null)
    setResult(null)
    Promise.all([DashboardCatalog(scope), ListDashboards(scope)])
      .then(([cat, list]) => {
        if (cat?.error || list?.error) return setError(cat?.error || list?.error)
        setMoneyCurrency(cat.currency)
        setCatalog(cat)
        setDashboards(list.dashboards)
        let remembered = null
        try {
          remembered = localStorage.getItem(ACTIVE_KEY(scope))
        } catch {}
        // a link to one dashboard (?dashboard=<a ready-made one's key, or an id>): opened, then out of the address
        let asked = null
        try {
          const url = new URL(window.location.href)
          const wanted = url.searchParams.get("dashboard")
          if (wanted) {
            asked = list.dashboards.find((d) => d.starter === wanted || d._id === wanted)?._id || null
            url.searchParams.delete("dashboard")
            window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash)
          }
        } catch {}
        setActiveId(asked || (list.dashboards.some((d) => d._id === remembered) ? remembered : list.dashboards[0]?._id))
      })
      .catch(() => setError("The dashboards could not be loaded"))
  }, [scope])

  const dashboard = useMemo(() => dashboards?.find((d) => d._id === activeId) || null, [dashboards, activeId])

  useEffect(() => {
    if (!activeId) return
    try {
      localStorage.setItem(ACTIVE_KEY(scope), activeId)
    } catch {}
  }, [activeId])

  // the figures, whenever what the dashboard shows changes
  const queryKey = dashboard ? JSON.stringify({ cards: dashboard.cards, range: dashboard.range, compare: dashboard.compare, filters: dashboard.filters || {} }) : ""
  useEffect(() => {
    if (!dashboard) return
    const run = ++queryRun.current
    // the figures moving is no reason to dim the cards: the new ones replace them
    if (!quiet.current) setLoading(true)
    quiet.current = false
    const timer = setTimeout(async () => {
      const res = await QueryDashboard({ scope, range: dashboard.range || { preset: "last_30_days" }, compare: dashboard.compare || { mode: "previous_period" }, filters: dashboard.filters || {}, cards: dashboard.cards.filter((c) => !c.hidden) }).catch(() => null)
      if (run !== queryRun.current) return
      setLoading(false)
      if (!res?.result) return setError(res?.error || "The figures could not be loaded")
      setError("")
      setResult(res)
    }, 150)
    return () => clearTimeout(timer)
  }, [queryKey, moved])

  // a change is shown at once and kept a moment later
  const update = useCallback(
    (patch) => {
      setDashboards((list) => list.map((d) => (d._id === activeId ? { ...d, ...patch } : d)))
      clearTimeout(saveTimer.current)
      const id = activeId
      saveTimer.current = setTimeout(() => SaveDashboard({ _id: id, scope, ...patch }).catch(() => {}), 500)
    },
    [activeId, scope]
  )

  const saveCard = (card) => {
    const cards = dashboard.cards.some((c) => c.id === card.id) ? dashboard.cards.map((c) => (c.id === card.id ? card : c)) : [...dashboard.cards, card]
    update({ cards })
    setEditing(null)
  }
  const removeCard = (card) => {
    update({ cards: dashboard.cards.filter((c) => c.id !== card.id) })
    setEditing(null)
  }

  const [naming, setNaming] = useState(false)
  const createDashboard = async (name) => {
    const res = await SaveDashboard({ scope, name, cards: [], range: dashboard?.range || { preset: "last_30_days" }, compare: dashboard?.compare || { mode: "previous_period" } })
    if (!res?.dashboard) throw new Error(res?.error === "too_many_dashboards" ? t`You have the most dashboards there can be` : t`The dashboard could not be created`)
    setDashboards((list) => [...list, res.dashboard])
    setActiveId(res.dashboard._id)
    setCustomizing(true)
  }
  const newDashboard = () => setNaming(true)

  const deleteDashboard = () =>
    showPromptDialog({
      title: t("Delete \"%s\"?", dashboard.name),
      message: t`The dashboard and its cards go; the figures behind them stay.`,
      confirmText: t`Delete`,
      cancelText: t`Cancel`,
      onConfirm: async () => {
        await DeleteDashboard(scope, dashboard._id).catch(() => {})
        const rest = dashboards.filter((d) => d._id !== dashboard._id)
        setDashboards(rest)
        setActiveId(rest[0]?._id || null)
        setCustomizing(false)
      },
    })

  if (error && !dashboards) return <div className="rounded-lg bg-admin_error/10 px-4 py-3 text-sm text-admin_error">{t(error)}</div>
  if (!dashboards || !catalog) return <div className="py-10 text-center text-sm text-admin_text_secondary">{t`Loading the dashboard…`}</div>

  const metricDef = (id) => catalog.metrics.find((m) => m.id === id)
  const tableDef = (id) => catalog.tables.find((t) => t.id === id)
  const comparing = !!result?.comparison

  return (
    <div className={`flex flex-col gap-4 ${className}`} data-testid="store-dashboards">
      {/* which dashboard, which dates, which filters: kept in sight while the cards scroll under it */}
      <div className={`sticky ${stickyTop} z-20 -mx-2 px-2 py-2 -my-2 flex flex-col gap-2 ${headerClassName}`} data-testid="dashboard-header">
      {/* on a phone two rows — the dashboard with Filters, Advisor and Customize, then the dates —
          the buttons as icons; from 768px one row: the dashboard at the left, the dates right before the buttons at the right. The three buttons are one group: when the row
          is short they wrap to the next one together */}
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        <Select
          className="order-1 flex-1 min-w-0 md:flex-none md:w-[240px]"
          value={activeId || ""}
          // a ready-made dashboard's name in the reader's language (a name of one's own stays as typed)
          options={[...dashboards.map((d) => [d._id, t(d.name)]), ["__new", t`+ New dashboard…`]]}
          onChange={(id) => (id === "__new" ? newDashboard() : setActiveId(id))}
          testId="dashboard-select"
        />
        {/* the header's parts, from the help center (helpKeys.header) */}
        <HelpHint helpKey={helpKeys.header} className="order-1 md:order-2" />
        <span className="hidden md:block md:order-2 flex-1" />
        {/* a phone's line break: what follows is the second row */}
        <span className="order-3 basis-full h-0 md:hidden" />
        {dashboard && (
          <PeriodPicker
            className="order-4 flex-1 min-w-0 md:order-3 md:flex-none"
            range={dashboard.range || { preset: "last_30_days" }}
            compare={dashboard.compare || { mode: "previous_period" }}
            period={result?.period}
            comparison={result?.comparison}
            onChange={({ range, compare }) => update({ range, compare })}
            timeZone={result?.timeZone}
          />
        )}
        <div className="order-2 md:order-4 flex items-center gap-2 md:gap-3 shrink-0" data-testid="dashboard-actions">
        {dashboard && (
          <div>
            <Filters scope={scope} value={dashboard.filters || {}} onChange={(filters) => update({ filters })} />
          </div>
        )}
        {/* advisor={false}: no advisor beside the dashboard */}
        {dashboard && advisor !== false && (
          <div>
          <Advisor
            {...advisor}
            scope={scope}
            catalog={catalog}
            context={{ range: dashboard.range || { preset: "last_30_days" }, compare: dashboard.compare || { mode: "previous_period" }, filters: dashboard.filters || {} }}
            // a card from the chat joins the dashboard as it was shown
            onAddCard={({ card }) => update({ cards: [...dashboard.cards, { ...card, id: `${card.id}a${Date.now().toString(36)}` }] })}
            // a whole dashboard from the chat: made as proposed, or its cards added to this one
            onAddCards={({ cards }) => update({ cards: [...dashboard.cards, ...cards.map((c, i) => ({ ...c, id: `${c.id}a${Date.now().toString(36)}${i}` }))] })}
            onCreateDashboard={async ({ name, cards, range, compare }) => {
              const res = await SaveDashboard({ scope, name, cards, range: range || dashboard.range, compare: compare || dashboard.compare })
              if (!res?.dashboard) return
              setDashboards((list) => [...list, res.dashboard])
              setActiveId(res.dashboard._id)
            }}
          />
          </div>
        )}
        {/* the name in a tooltip while the button is the icon alone (below 2xl) */}
        <Tooltip text={t`Customize`} position="bottom" variant="admin" className="2xl:hidden">
          <Button variant="admin_secondary" className="max-2xl:!px-3" onClick={() => setCustomizing(true)} disabled={!dashboard} data-testid="dashboard-customize-open" aria-label={t`Customize`}>
            <span className="flex items-center gap-1.5"><AdjustmentsHorizontalIcon className="w-5 h-5 2xl:w-4 2xl:h-4" /><span className="hidden 2xl:inline">{t`Customize`}</span></span>
          </Button>
        </Tooltip>
        {/* the cards and how they are composed, from the help center (helpKeys.cards) */}
        <HelpHint helpKey={helpKeys.cards} align="end" />
        </div>
      </div>

      {dashboard && <ActiveFilters scope={scope} value={dashboard.filters || {}} onChange={(filters) => update({ filters })} />}
      </div>

      {error && <div className="rounded-lg bg-admin_error/10 px-4 py-2 text-sm text-admin_error">{t(error)}</div>}

      {dashboard && !dashboard.cards.length && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-admin_bordered py-12 text-center">
          <span className="text-sm text-admin_text_secondary">{t`This dashboard has no cards yet.`}</span>
          <Button variant="admin_primary" onClick={() => setEditing("new")}>
            <span className="flex items-center gap-1"><PlusIcon className="w-4 h-4" />{t`Add a card`}</span>
          </Button>
        </div>
      )}

      {dashboard && dashboard.cards.length > 0 && dashboard.cards.every((c) => c.hidden) && (
        <div className="rounded-xl border border-dashed border-admin_bordered py-10 text-center text-sm text-admin_text_secondary">
          {t`Every card is hidden.`}{" "}
          <button type="button" onClick={() => setCustomizing(true)} className="font-semibold text-admin_primary hover:underline">
            {t`Show them from Customize`}
          </button>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* a hidden card stays on the dashboard, out of sight: shown again from Customize */}
        {(dashboard?.cards || []).filter((card) => !card.hidden).map((card) => {
          const data = result?.cards?.[card.id]
          const props = { card, data, loading, onEdit: () => setEditing(card) }
          return (
            <div key={card.id} className={SPAN[card.size || "s"] || SPAN.s}>
              {card.type === "metric" && <MetricCard {...props} def={metricDef(card.metric)} comparing={comparing} />}
              {/* a chart of how things stand now (the catalog) has no course: its figure, as a number */}
              {card.type === "chart" && metricDef(card.metric)?.timeless && <MetricCard {...props} def={metricDef(card.metric)} comparing={false} note="now" />}
              {card.type === "chart" && !metricDef(card.metric)?.timeless && <ChartCard {...props} def={metricDef(card.metric)} period={result?.period} comparison={result?.comparison} timeZone={result?.timeZone} dimensions={catalog.dimensions} />}
              {card.type === "segments" && <SegmentsCard {...props} def={metricDef(card.metric)} comparing={comparing} dimensions={catalog.dimensions} />}
              {card.type === "table" && <TableCard {...props} def={tableDef(card.table)} comparing={comparing} onSort={(sort) => saveCard({ ...card, sort })} />}
            </div>
          )
        })}
      </div>


      <NameDialog
        isOpen={naming}
        onClose={() => setNaming(false)}
        onCreate={createDashboard}
        title={t`New dashboard`}
        placeholder={t`Sales overview`}
        hint={t`It starts empty: add the cards you want to see.`}
        cta={t`Create`}
        testId="new-dashboard"
      />
      <CardEditor isOpen={!!editing} onClose={() => setEditing(null)} card={editing === "new" ? null : editing} catalog={catalog} onSave={saveCard} onDelete={removeCard} />
      <CustomizePanel
        isOpen={customizing}
        onClose={() => setCustomizing(false)}
        dashboard={dashboard}
        catalog={catalog}
        onRename={(name) => update({ name })}
        onReorder={(cards) => update({ cards })}
        onEdit={(card) => setEditing(card)}
        onToggle={(card) => update({ cards: dashboard.cards.map((c) => (c.id === card.id ? { ...c, hidden: !c.hidden } : c)) })}
        onRemove={removeCard}
        onAdd={() => setEditing("new")}
        onDeleteDashboard={deleteDashboard}
        canDelete={dashboards.length > 1}
      />
    </div>
  )
}
